// frontend/src/pages/HomePage.tsx
// ───────────────────────────────────────────────────────────────────
// Racine `/`. Pour un visiteur NON connecté : page d'accueil minimale + liens
// légaux (inchangée). Pour un utilisateur DÉJÀ connecté : redirection vers la
// home de son rôle (TASK-038 / SAUX-63) — avant, un superAdmin revenant sur
// `quarte.app` atterrissait sur cette page sans aucun lien vers son espace.
//
// La redirection vit ICI plutôt que dans le router : la cible dépend des claims,
// donc de la session, et `/` doit rester une route publique sans garde.
//
// Garde anti-boucle : on ne redirige que si le rôle a une home DÉDIÉE
// (`hasDedicatedHome`). Un `readonly` ou un `restaurantManager`, dont la home
// est `/`, reste sur cette page — sinon `/` → `/` en boucle.
// ───────────────────────────────────────────────────────────────────

import { Link, Navigate } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { hasDedicatedHome, redirectToRoleHome } from '@/lib/role-redirect';
import { LEGAL_LINKS } from '@/pages/public/legal/LegalLayout';

import type { JSX } from 'react';

export function HomePage(): JSX.Element {
  const { user, claims, loading } = useAuth();

  if (loading) {
    return <p className="p-8 text-slate-600">Chargement de la session…</p>;
  }

  // Session établie ET claims lues → on emmène l'utilisateur chez lui.
  if (user !== null && claims !== null && hasDedicatedHome(claims.role)) {
    return <Navigate to={redirectToRoleHome(claims.role)} replace />;
  }

  return (
    <div className="flex min-h-screen flex-col">
      <main className="mx-auto w-full max-w-2xl flex-1 p-8">
        <h1 className="text-2xl font-bold text-slate-900">MenuDigital Pro</h1>
        {user === null ? (
          <p className="mt-4 text-slate-600">
            Vous n'êtes pas connecté.{' '}
            <a className="font-medium text-slate-900 underline" href="/signin">
              Se connecter
            </a>
            .
          </p>
        ) : (
          <p className="mt-4 text-slate-600">
            Connecté en tant que <strong>{user.email ?? user.uid}</strong>
            {claims ? (
              <>
                {' '}
                — rôle <strong>{claims.role}</strong>
              </>
            ) : null}
          </p>
        )}
      </main>
      <footer className="border-t border-slate-200 px-8 py-6">
        <ul className="mx-auto flex max-w-2xl flex-wrap gap-x-6 gap-y-2 text-sm">
          {LEGAL_LINKS.map((link) => (
            <li key={link.to}>
              <Link to={link.to} className="text-slate-500 underline hover:text-slate-800">
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </footer>
    </div>
  );
}
